/**
 * CountryFilter — sharp, editorial country dropdown for the /shop sidebar.
 *
 * Reads distinct `ships_from` values from `/api/shop/countries` with live
 * product counts. Selecting a country updates the URL search-param
 * `?country=India` and the parent ShopHome refetches.
 *
 * Also renders a small flag glyph for the top-N Indian and international
 * markets. Falls back to a globe icon for unknown countries.
 */
import React from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const API = process.env.REACT_APP_BACKEND_URL as string;
const shopApi = axios.create({ baseURL: `${API}/api/shop` });

interface Props {
  value: string | null;
  onChange: (country: string | null) => void;
  className?: string;
}

// A tiny inline flag map for the common markets — no image assets needed.
const FLAG: Record<string, string> = {
  india: "🇮🇳",
  "united states": "🇺🇸",
  usa: "🇺🇸",
  us: "🇺🇸",
  "united kingdom": "🇬🇧",
  uk: "🇬🇧",
  uae: "🇦🇪",
  "united arab emirates": "🇦🇪",
  singapore: "🇸🇬",
  australia: "🇦🇺",
  germany: "🇩🇪",
  japan: "🇯🇵",
  china: "🇨🇳",
  canada: "🇨🇦",
  bangladesh: "🇧🇩",
  "sri lanka": "🇱🇰",
  nepal: "🇳🇵",
  pakistan: "🇵🇰",
};

function flagFor(name: string): string {
  return FLAG[name.toLowerCase()] || "";
}

export const CountryFilter: React.FC<Props> = ({ value, onChange, className = "" }) => {
  const q = useQuery<{ countries: { country: string; count: number }[]; total: number }>({
    queryKey: ["shop-countries"],
    queryFn: async () => (await shopApi.get("/countries", { params: { limit: 50 } })).data,
    staleTime: 5 * 60_000,
  });

  const list = q.data?.countries || [];
  const selected = value && list.find(c => c.country.toLowerCase() === value.toLowerCase());
  const label = selected ? selected.country : "All countries";
  const emoji = value ? flagFor(value) : "";

  return (
    <div className={className} data-testid="shop-country-filter">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5 font-display font-semibold">
        Ships from
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="shop-country-trigger"
            className="w-full inline-flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-medium text-slate-800 dark:text-slate-100 hover:border-slate-900 dark:hover:border-white transition-colors"
          >
            <span className="text-base leading-none">
              {emoji || <Globe className="h-4 w-4 text-slate-500" />}
            </span>
            <span className="flex-1 text-left truncate">{label}</span>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-1 max-h-72 overflow-y-auto">
          <button
            type="button"
            data-testid="shop-country-all"
            onClick={() => onChange(null)}
            className={`w-full text-left px-2 py-1.5 rounded text-sm inline-flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 ${!value ? "font-semibold text-orange-600" : ""}`}
          >
            <Globe className="h-3.5 w-3.5" />
            <span className="flex-1">All countries</span>
            {!value && <Check className="h-3.5 w-3.5" />}
          </button>
          {q.isLoading && (
            <div className="text-xs text-slate-400 px-2 py-2">Loading…</div>
          )}
          {list.map((c) => {
            const active = value?.toLowerCase() === c.country.toLowerCase();
            const em = flagFor(c.country);
            return (
              <button
                key={c.country}
                type="button"
                data-testid={`shop-country-opt-${c.country.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => onChange(c.country)}
                className={`w-full text-left px-2 py-1.5 rounded text-sm inline-flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 ${active ? "font-semibold text-orange-600" : ""}`}
              >
                <span className="text-base leading-none">{em || <Globe className="h-3.5 w-3.5" />}</span>
                <span className="flex-1 truncate">{c.country}</span>
                <span className="text-[10px] tabular-nums text-slate-400">{c.count.toLocaleString()}</span>
                {active && <Check className="h-3.5 w-3.5" />}
              </button>
            );
          })}
          {!q.isLoading && list.length === 0 && (
            <div className="text-xs text-slate-500 dark:text-slate-400 px-2 py-3">
              No country data yet — sellers set this at listing time.
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default CountryFilter;
